// src/components/Teabates/TeabateStoryContainer.tsx
import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronUp, ChevronDown, Send } from 'lucide-react';
import api from 'config/axiosConfig';
import { TeabateObject } from './types';
import TeabateArgument from './TeabateArgument';
import TeabateArgumentReply from './TeabateArgumentReply';
import EntityAvatar from 'components/Entities/EntityAvatar';
import { sanitize } from 'config/xssConfig';
import { apiRoutes } from "util/apiRoutes";

interface TeabateStoryContainerProps {
  issueId: number;
  onClose: () => void;
}

interface TeabateInputProps {
  onSubmit: (content: string, type: 'agree' | 'disagree') => void;
  type: 'agree' | 'disagree';
}

const TeabateInput: React.FC<TeabateInputProps> = ({ onSubmit, type }) => {
  const [content, setContent] = useState('');

  const handleSubmit = () => {
    if (!content.trim()) return;
    onSubmit(sanitize(content), type);
    setContent('');
  };

  return (
    <div className="flex items-center gap-2 p-2 bg-gray-800 bg-opacity-70 rounded-lg">
      <input
        type="text"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={`Add ${type === 'agree' ? 'supporting' : 'opposing'} statement...`}
        className="flex-grow py-2 px-3 bg-gray-700 rounded-full text-white text-sm focus:outline-none"
      />
      <button 
        onClick={handleSubmit}
        disabled={!content.trim()}
        className={`p-2 rounded-full ${!content.trim() ? 'bg-gray-600 text-gray-400' : 'bg-blue-600 text-white'}`}
      >
        <Send size={16} />
      </button>
    </div>
  );
};

const TeabateStoryContainer: React.FC<TeabateStoryContainerProps> = ({ issueId, onClose }) => {
  const [currentTeabate, setCurrentTeabate] = useState<TeabateObject | null>(null);
  const [teabateArguments, setTeabateArguments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingArguments, setLoadingArguments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [selectedArgument, setSelectedArgument] = useState<any | null>(null);
  
  const topSectionRef = useRef<HTMLDivElement>(null);
  const bottomSectionRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number | null>(null);

  // Determine which type is on top for current level
  const topType: 'agree' | 'disagree' = currentLevel % 2 === 0 ? 'agree' : 'disagree';
  const bottomType: 'agree' | 'disagree' = topType === 'agree' ? 'disagree' : 'agree';

  // Fetch teabates for the given issue
  const fetchTeabates = async () => {
    try {
      setLoading(true);
      const response = await api.get(apiRoutes.posts.root, { params: { kind: "teabate", issueId } });
      if (response.data && response.data.length > 0) {
        setCurrentTeabate(response.data[0]);
        setError(null);
        // After setting current teabate, fetch its arguments
        fetchArgumentsForTeabate(response.data[0].id);
      } else {
        setError('No teabates found for this issue.');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load teabates.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch arguments for a specific teabate
  const fetchArgumentsForTeabate = async (teabateId: number) => {
    try {
      setLoadingArguments(true);
      const response = await api.get(apiRoutes.comments.teabateArguments(teabateId));
      setTeabateArguments(response.data);
    } catch (err) {
      console.error('Error fetching teabate arguments:', err);
    } finally {
      setLoadingArguments(false);
    }
  };

  // Fetch arguments when teabate or level changes
  useEffect(() => {
    if (currentTeabate?.id) {
      fetchArgumentsForTeabate(currentTeabate.id);
    }
  }, [currentTeabate?.id, currentLevel]);

  // Initial fetch
  useEffect(() => {
    fetchTeabates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId]);

  // Handle touch events for level navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY.current === null) return;
    
    const touchEndY = e.changedTouches[0].clientY;
    const diff = touchEndY - touchStartY.current;
    
    // Swipe up = next level, swipe down = previous level
    if (diff < -50) {
      setCurrentLevel(prev => prev + 1);
    } else if (diff > 50 && currentLevel > 0) {
      setCurrentLevel(prev => prev - 1);
    }
    
    touchStartY.current = null;
  };

  // Handle new argument submission
  const handleSubmitArgument = async (content: string, type: 'agree' | 'disagree') => {
    if (!currentTeabate) return;
    
    try {
      const payload = {
        content,
        argument_type: type,
        parent_id: selectedArgument?.id || null,
        is_anonymous: false
      };
      
      await api.post(apiRoutes.comments.teabateArgument(currentTeabate.id), payload);
      
      // Refresh arguments after submitting
      fetchArgumentsForTeabate(currentTeabate.id);
      setShowReplyInput(false);
      setSelectedArgument(null);
    } catch (err) {
      console.error('Error submitting argument:', err);
    }
  };

  const handleArgumentVote = async (type: 'upvote' | 'downvote', argumentId: number) => {
    try {
      await api.post(apiRoutes.votes.comment(argumentId), { value: type === "upvote" ? 1 : -1 });
      fetchArgumentsForTeabate(currentTeabate!.id);
    } catch (error) {
      console.error('Error voting on argument:', error);
    }
  };

  const handleArgumentReply = (argument: any) => {
    setSelectedArgument(argument);
    setShowReplyInput(true);
  };

  // Filter arguments by type
  const topArguments = teabateArguments.filter(arg => 
    arg.properties?.argument_type === topType && arg.properties?.level === currentLevel
  );
  
  // Bottom arguments should be from current level + 1
  const bottomArguments = teabateArguments.filter(arg => 
    arg.properties?.argument_type === bottomType && 
    arg.properties?.level === currentLevel + 1
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full w-full bg-black">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  if (error || !currentTeabate) {
    return (
      <div className="flex flex-col justify-center items-center h-full w-full bg-black text-white p-4">
        <p className="text-lg mb-4">{error}</p>
        <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full">
          Close
        </button>
      </div>
    );
  }

  return (
    <div 
      className="h-full w-full relative bg-black"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 px-4 py-3 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center gap-2">
          {currentTeabate && (
            <EntityAvatar
              entity={currentTeabate as any}
              entityType="teabate"
              showLink={false}
              badgeText={`Teabate`}
            />
          )}
          <div className="flex flex-col ml-2">
            <p className="text-white text-sm font-medium">
              Round {currentLevel + 1} • {topType === 'agree' ? 'Supporting' : 'Opposing'} Arguments
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-white p-1">
          <X size={24} />
        </button>
      </div>

      {/* Top Section (Main Arguments) */}
      <div 
        ref={topSectionRef}
        className="h-1/2 w-full overflow-y-auto pt-16 pb-2 px-4 border-b border-gray-700"
      >
        {loadingArguments ? (
          <div className="h-full w-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
          </div>
        ) : topArguments.length > 0 ? (
          <div className="space-y-4">
            {topArguments.map(argument => (
              <TeabateArgument 
                key={argument.id}
                argument={argument}
                onReply={handleArgumentReply}
                onVote={handleArgumentVote}
              />
            ))}
          </div>
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <p className="text-gray-400">No {topType} arguments at this level yet.</p>
          </div>
        )}
        
        {/* Level navigation indicators */}
        {currentLevel > 0 && (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 text-gray-400">
            <ChevronUp size={20} />
            <span className="text-xs">Previous Round</span>
          </div>
        )}
      </div>

      {/* Bottom Section (Next Round Arguments) */}
      <div 
        ref={bottomSectionRef}
        className="h-1/2 w-full overflow-y-auto pt-4 pb-16 px-4"
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-medium text-white">
            Round {currentLevel + 2} • {bottomType === 'agree' ? 'Supporting' : 'Opposing'} Arguments
          </h3>
        </div>
        
        {loadingArguments ? (
          <div className="h-full w-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
          </div>
        ) : bottomArguments.length > 0 ? (
          <div className="space-y-4">
            {bottomArguments.map(argument => (
              <TeabateArgument 
                key={argument.id}
                argument={argument}
                onReply={() => {}}
                onVote={handleArgumentVote}
              />
            ))}
          </div>
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <p className="text-gray-400">No arguments for the next round yet.</p>
          </div>
        )}
        
        {/* Level navigation indicators */}
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 text-gray-400">
          <span className="text-xs">Next Round</span>
          <ChevronDown size={20} />
        </div>
      </div>
      
      {/* Reply Input */}
      {showReplyInput ? (
        <div className="absolute bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800">
          <TeabateArgumentReply
            teabateId={currentTeabate.id}
            parentArgument={selectedArgument}
            onReplySubmitted={() => {
              setShowReplyInput(false);
              fetchArgumentsForTeabate(currentTeabate.id);
            }}
            onCancel={() => {
              setShowReplyInput(false);
              setSelectedArgument(null);
            }}
          />
        </div>
      ) : (
        <div className="absolute bottom-2 left-0 right-0 px-4">
          <TeabateInput 
            onSubmit={(content, type) => handleSubmitArgument(content, type)} 
            type={selectedArgument ? bottomType : bottomType}
          />
        </div>
      )}
    </div>
  );
};

export default TeabateStoryContainer;
